import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heartbeat, ChartLine, ChartDonut, Warning } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Button } from '../components/ui/button';
import { BACKEND_URL } from '../config';

const HEALTH_COLORS = {
  Healthy: '#22c55e',
  'At Risk': '#eab308',
  Critical: '#ef4444',
};

const ClientHealthWidget = () => {
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [view, setView] = useState('donut');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [healthRes, trendRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/clients/health-scores`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/clients/health-trend`, { withCredentials: true }),
        ]);
        setData(healthRes.data);
        setTrendData(trendRes.data);
      } catch (err) { console.error(err.message);
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading || !data?.summary) return null;

  const chartData = [
    { name: 'Healthy', value: data.summary.healthy, color: HEALTH_COLORS.Healthy },
    { name: 'At Risk', value: data.summary.at_risk, color: HEALTH_COLORS['At Risk'] },
    { name: 'Critical', value: data.summary.critical, color: HEALTH_COLORS.Critical },
  ].filter(d => d.value > 0);

  const total = chartData.reduce((acc, d) => acc + d.value, 0);

  const DonutTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
    return (
      <div className="bg-card border border-border rounded-sm px-3 py-2 shadow-lg">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
          <span className="text-sm font-semibold">{d.name}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{d.value} clients ({pct}%)</div>
      </div>
    );
  };

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-sm px-3 py-2.5 shadow-lg">
        <div className="text-xs font-bold mb-1.5">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}:</span>
            <span className="font-bold">{p.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const alerts = trendData?.alerts || [];

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="client-health-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-sm bg-green-500/10 flex items-center justify-center">
            <Heartbeat size={20} weight="fill" className="text-green-500" />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight">Client Health</div>
            <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground">RFM SCORING</div>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-sm p-0.5">
          <button
            onClick={() => setView('donut')}
            className={`p-1.5 rounded-sm transition-colors ${view === 'donut' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
            title="Distribution"
            data-testid="health-view-donut"
          >
            <ChartDonut size={16} weight={view === 'donut' ? 'fill' : 'regular'} className="text-foreground" />
          </button>
          <button
            onClick={() => setView('trend')}
            className={`p-1.5 rounded-sm transition-colors ${view === 'trend' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
            title="Trend"
            data-testid="health-view-trend"
          >
            <ChartLine size={16} weight={view === 'trend' ? 'fill' : 'regular'} className="text-foreground" />
          </button>
        </div>
      </div>

      {view === 'donut' ? (
        <div className="flex items-center gap-6">
          <div className="w-[140px] h-[140px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={65}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<DonutTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-3">
            {chartData.map((d) => {
              const pct = total > 0 ? ((d.value / total) * 100).toFixed(0) : 0;
              return (
                <div key={d.name} className="flex items-center justify-between" data-testid={`health-stat-${d.name.toLowerCase().replace(' ', '-')}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-sm">{d.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold tabular-nums">{d.value}</span>
                    <span className="text-xs text-muted-foreground ml-1.5">({pct}%)</span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Total Clients</span>
              <span className="text-sm font-bold tabular-nums">{total}</span>
            </div>
          </div>
        </div>
      ) : (
        <div data-testid="health-trend-chart">
          {trendData?.months?.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={trendData.months} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--foreground))"
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--foreground))" />
                  <Tooltip content={<TrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="healthy"
                    name="Healthy"
                    stackId="1"
                    stroke={HEALTH_COLORS.Healthy}
                    fill={HEALTH_COLORS.Healthy}
                    fillOpacity={0.7}
                  />
                  <Area
                    type="monotone"
                    dataKey="at_risk"
                    name="At Risk"
                    stackId="1"
                    stroke={HEALTH_COLORS['At Risk']}
                    fill={HEALTH_COLORS['At Risk']}
                    fillOpacity={0.7}
                  />
                  <Area
                    type="monotone"
                    dataKey="critical"
                    name="Critical"
                    stackId="1"
                    stroke={HEALTH_COLORS.Critical}
                    fill={HEALTH_COLORS.Critical}
                    fillOpacity={0.7}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Trend legend */}
              <div className="flex items-center justify-center gap-5 mt-3">
                {[
                  { label: 'Healthy', color: HEALTH_COLORS.Healthy },
                  { label: 'At Risk', color: HEALTH_COLORS['At Risk'] },
                  { label: 'Critical', color: HEALTH_COLORS.Critical },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="text-xs text-muted-foreground">{l.label}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No trend data available</div>
          )}
        </div>
      )}

      {/* Spike Alerts */}
      {alerts.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="health-alerts">
          {alerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 p-2.5 rounded-sm text-xs ${
                a.severity === 'high'
                  ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                  : 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
              }`}
              data-testid={`health-alert-${i}`}
            >
              <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientHealthWidget;
